import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Download, Loader2, Mail, MessageCircleHeart, Moon, PenLine, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { Page } from "@/components/app/AppKit";
import { VoiceButton } from "@/components/quantum/TeachingCards";
import Seal from "@/components/quantum/Seal";
import LoveNote from "@/components/quantum/LoveNote";

const FILTERS = [["all", "Everything"], ["partner", "Partner"], ["ariel", "Ariel"], ["seals", "Seals"], ["reflections", "Reflections"], ["keepsakes", "Keepsakes"]];
const when = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

const Group = ({ id, icon: Icon, title, count, children, empty }) => (
  <section data-testid={`box-${id}`} className="mt-10">
    <p className="flex items-center gap-2 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]"><Icon className="w-3.5 h-3.5" /> {title} · {count}</p>
    {count ? children : <p className="mt-4 text-[15px] text-[#A8998C]">{empty}</p>}
  </section>
);

const Messages = ({ list }) => {
  const hearts = list.filter((m) => m.kind === "heart");
  const notes = list.filter((m) => m.kind === "text");
  const received = hearts.filter((m) => !m.mine).length;
  return (
    <Group id="partner" icon={Users} title="Partner messages" count={list.length} empty="Hearts and notes from your partner sessions will be kept here.">
      {hearts.length > 0 && <p data-testid="box-hearts" className="mt-4 flex items-center gap-2 text-[15px] text-[#CFC2B6]"><Heart className="w-4 h-4 fill-[#E8838E] text-[#E8838E]" /> {received} heart{received === 1 ? "" : "s"} received · {hearts.length - received} sent</p>}
      <ul className="mt-4 space-y-3">
        {notes.map((m, k) => (
          <li key={`${m.at}-${k}`} data-testid={`box-message-${k}`} className={`border p-4 ${m.mine ? "border-[#D9B47A]/20" : "border-[#E8838E]/30 bg-[#140910]"}`}>
            <p className="font-display italic text-lg text-[#F2E8DF]">“{m.text}”</p>
            <p className="mt-2 text-[12px] text-[#A8998C]">{m.mine ? `You to ${m.with}` : `From ${m.with}`} · {m.practice || m.gate} · {when(m.at)}</p>
          </li>
        ))}
      </ul>
    </Group>
  );
};

const NoteWriter = ({ sessions, onSent }) => {
  const [code, setCode] = useState("");
  const [open, setOpen] = useState(false);
  if (!sessions.length) return null;
  const chosen = sessions.find((x) => x.code === code) ?? sessions[0];
  if (!open) return <button data-testid="box-love-note-open" onClick={() => setOpen(true)} className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#E8838E]/50 px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706] transition-colors"><Mail className="w-4 h-4" /> Write a love note</button>;
  return (
    <div className="mt-5">
      <label className="block text-[13px] text-[#A8998C]">From the session</label>
      <select data-testid="box-love-note-session" value={chosen.code} onChange={(e) => setCode(e.target.value)} className="mt-2 mb-4 w-full bg-[#0F070B] border border-[#D9B47A]/25 px-4 py-3 text-[16px] text-[#F2E8DF] focus:outline-none focus:border-[#E8838E]">
        {sessions.map((x) => <option key={x.code} value={x.code}>{`With ${x.with} · ${x.gate} · ${when(x.at)}`}</option>)}
      </select>
      <LoveNote code={chosen.code} partner={chosen.with} testId="box-love-note" onSent={() => { setOpen(false); onSent(); }} />
    </div>
  );
};

const LoveNotes = ({ list, sessions, onSent }) => (
  <>
    <Group id="love-notes" icon={Mail} title="Love notes" count={list.length} empty={sessions.length ? "Longer letters you and your partner write after a session are kept here." : "After your first shared session, you and your partner can write each other love notes here."}>
      <ul className="mt-4 space-y-4">
        {list.map((n, k) => (
          <li key={`${n.at}-${k}`} data-testid={`box-love-note-${k}`} className={`border p-6 ${n.mine ? "border-[#D9B47A]/25" : "border-[#E8838E]/40 bg-[linear-gradient(160deg,#1A0B12,#0F070B)]"}`}>
            <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#E8838E]">{n.mine ? `You to ${n.with}` : `From ${n.with}`} · {n.practice || n.gate} · {when(n.at)}</p>
            <p className="mt-4 font-display italic text-[18px] leading-relaxed text-[#F2E8DF] whitespace-pre-wrap break-words">{n.text}</p>
          </li>
        ))}
      </ul>
    </Group>
    <NoteWriter sessions={sessions} onSent={onSent} />
  </>
);

const ArielReplies = ({ list }) => (
  <Group id="ariel" icon={MessageCircleHeart} title="Ariel’s words to you" count={list.length} empty="When Ariel responds to your reflections and check-ins, her words are kept here.">
    <ul className="mt-4 space-y-3">
      {list.map((a, k) => (
        <li key={k} data-testid={`box-ariel-${k}`} className="border-l-2 border-[#E8838E] bg-[#140910] p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#E8838E]">Gate {a.g + 1} · {a.gate} · after your {a.kind}</p>
            <VoiceButton text={a.text} testId={`box-ariel-voice-${k}`} />
          </div>
          <p className="mt-3 font-display italic text-[18px] leading-relaxed text-[#F2E8DF]">{a.text}</p>
        </li>
      ))}
    </ul>
  </Group>
);

const Seals = ({ list, names }) => (
  <Group id="seals" icon={Sparkles} title="Gate seals" count={list.length} empty="Complete a gate’s check-in and its seal is placed in your box.">
    <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-4">
      {list.map((g) => (
        <Link key={g} to={`/quantum/gate/${g}`} data-testid={`box-seal-${g}`} className="flex flex-col items-center text-center">
          <Seal g={g} earned size={76} />
          <span className="mt-2 text-[12px] text-[#CFC2B6] leading-tight">{names[g] ?? `Gate ${g + 1}`}</span>
        </Link>
      ))}
    </div>
  </Group>
);

const Reflections = ({ list }) => (
  <Group id="reflections" icon={PenLine} title="Your reflections" count={list.length} empty="What you write in each gate’s reflection is kept privately here.">
    <ul className="mt-4 space-y-4">
      {list.map((r) => (
        <li key={r.g} data-testid={`box-reflection-${r.g}`} className="border border-[#D9B47A]/20 p-5">
          <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#D9B47A]">Gate {r.g + 1} · {r.gate}{r.felt ? ` · felt: ${r.felt}` : ""}</p>
          {r.items.map((x, k) => (
            <div key={k} className="mt-4"><p className="text-[14px] text-[#A8998C]">{x.q}</p><p className="mt-1 text-[16px] leading-relaxed text-[#F2E8DF] whitespace-pre-wrap">{x.a}</p></div>
          ))}
        </li>
      ))}
    </ul>
  </Group>
);

const Keepsakes = ({ list }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { downloadKeepsake } = await import("@/lib/keepsake");
      await downloadKeepsake({ name: user?.name?.split(" ")[0] || "an initiate", items: list.slice(0, 13).reverse() });
      toast.success("Your keepsake is downloading.");
    } catch {
      toast.error("The keepsake couldn’t be made. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Group id="keepsakes" icon={Moon} title="Moon and intention keepsakes" count={list.length} empty="Intentions you set at the new moon in 13 Gates to Ecstasy appear here as keepsakes.">
      <button data-testid="box-keepsake-download" onClick={download} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#D9B47A] text-[#0A0706] px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download keepsake</button>
      <ul className="mt-4 space-y-3">
        {list.map((i) => (
          <li key={i.cycle} data-testid={`box-intention-${i.cycle.slice(0, 10)}`} className="border border-[#D9B47A]/20 p-4">
            <p className="font-tt-mono text-[10px] uppercase tracking-[0.2em] text-[#A8998C]">New moon · {when(i.cycle)}{i.bloomed ? " · bloomed" : ""}</p>
            <p className="mt-2 font-display italic text-lg text-[#F2E8DF]">“{i.text}”</p>
            {i.note && <p className="mt-2 text-[14px] text-[#E8838E]">{i.note}</p>}
          </li>
        ))}
      </ul>
    </Group>
  );
};

// Journal tab: the Sacred Box keeps every partner message, Ariel's words, seals, reflections and moon keepsakes.
export default function SacredBox({ names }) {
  const [box, setBox] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/quantum/sacred-box").then(({ data }) => setBox(data ?? {})).catch(() => setBox({}));
  useEffect(() => {
    load();
  }, []);
  const show = (k) => filter === "all" || filter === k;
  const b = { messages: [], love_notes: [], sessions: [], ariel: [], seals: [], reflections: [], intentions: [], ...box };
  const total = b.messages.length + b.love_notes.length + b.ariel.length + b.seals.length + b.reflections.length + b.intentions.length;
  return (
    <Page testId="quantum-journal" eyebrow="Journal" title={<>The Sacred <em className="text-[#E8838E]">Box.</em></>}>
      <motion.div initial={{ opacity: 0.4, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="mt-6 relative overflow-hidden border border-[#D9B47A]/30 bg-[linear-gradient(160deg,#1A0B12,#0B0509)] p-6">
        <span aria-hidden className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#C2414F]/20 blur-3xl" />
        <p className="relative text-[15px] leading-relaxed text-[#CFC2B6]">Everything you are given and everything you give along the path, kept for you alone.</p>
        <p data-testid="box-total" className="relative mt-3 font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">{box ? `${total} treasure${total === 1 ? "" : "s"} kept` : "Opening…"}</p>
      </motion.div>
      <div data-testid="box-filters" className="mt-6 -mx-5 px-5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
        {FILTERS.map(([k, l]) => (
          <button key={k} data-testid={`box-filter-${k}`} onClick={() => setFilter(k)} aria-pressed={filter === k} className={`shrink-0 rounded-full border px-4 py-2 text-[13px] transition-colors ${filter === k ? "bg-[#D9B47A] border-[#D9B47A] text-[#0A0706]" : "border-[#D9B47A]/25 text-[#CFC2B6]"}`}>{l}</button>
        ))}
      </div>
      {!box ? <Loader2 className="mt-10 w-5 h-5 animate-spin text-[#D9B47A]" /> : (
        <>
          {show("partner") && <LoveNotes list={Array.isArray(b.love_notes) ? b.love_notes : []} sessions={Array.isArray(b.sessions) ? b.sessions : []} onSent={load} />}
          {show("partner") && <Messages list={b.messages} />}
          {show("ariel") && <ArielReplies list={b.ariel} />}
          {show("seals") && <Seals list={b.seals} names={names} />}
          {show("reflections") && <Reflections list={b.reflections} />}
          {show("keepsakes") && <Keepsakes list={b.intentions} />}
        </>
      )}
    </Page>
  );
}
